import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/hooks/useAuth";
import { GlobalAdminProvider } from "@/hooks/useGlobalAdmin";
import { SiteAdminProvider } from "@/hooks/useSiteAdmin";
import { LoanOfficerAuthProvider } from "@/hooks/useLoanOfficerAuth";
import { GoogleAnalyticsProvider } from "@/components/analytics/GoogleAnalyticsProvider";
import Index from "./pages/Index";
import Setup from "./pages/public/Setup";
import Sitemap from "./pages/public/Sitemap";
import Robots from "./pages/public/Robots";
import AdminLogin from "./pages/admin/Login";
import ResetPassword from "./pages/admin/ResetPassword";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminProfile from "./pages/admin/Profile";
import SiteSettings from "./pages/admin/SiteSettings";
import TownSetup from "./pages/admin/TownSetup";
import TownManage from "./pages/admin/TownManage";
import TownContentEdit from "./pages/admin/TownContentEdit";
import AreaSetup from "./pages/admin/AreaSetup";
import AreaManage from "./pages/admin/AreaManage";
import AreaContentEdit from "./pages/admin/AreaContentEdit";
import TownPage from "./pages/public/TownPage";
import AreaPage from "./pages/public/AreaPage";
import TownsIndex from "./pages/public/TownsIndex";
import MarketValueReportPage from "./pages/public/MarketValueReportPage";
import CommunityGuidePage from "./pages/public/CommunityGuidePage";
import MortgageInfoPage from "./pages/public/MortgageInfoPage";
import BuyersPage from "./pages/public/BuyersPage";
import SellersPage from "./pages/public/SellersPage";
import BlogIndex from "./pages/public/BlogIndex";
import BlogPost from "./pages/public/BlogPost";
import BlogManage from "./pages/admin/BlogManage";
import BlogEdit from "./pages/admin/BlogEdit";
import BlogPreview from "./pages/admin/BlogPreview";
import LeadsManage from "./pages/admin/LeadsManage";
import LoanOfficersManage from "./pages/admin/LoanOfficersManage";
import LoanOfficerEdit from "./pages/admin/LoanOfficerEdit";
import SitePageEdit from "./pages/admin/SitePageEdit";
import WizardPage from "./pages/agent/Wizard";
import AgentDashboard from "./pages/agent/AgentDashboard";
import LoanOfficerDashboard from "./pages/loanofficer/LoanOfficerDashboard";
import AdsManage from "./pages/admin/AdsManage";
import HomepageEdit from "./pages/admin/HomepageEdit";
import SearchConsole from "./pages/admin/SearchConsole";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,  // 5 minutes - data stays fresh
      gcTime: 30 * 60 * 1000,    // 30 minutes - keep in cache
      refetchOnWindowFocus: false,  // Don't refetch when tab gains focus
      refetchOnMount: false,        // Don't refetch if data exists
      retry: 1,                     // Only retry once on failure
    },
  },
});

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <GlobalAdminProvider>
          <SiteAdminProvider>
            <LoanOfficerAuthProvider>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                <BrowserRouter>
                  <GoogleAnalyticsProvider>
                  <Routes>
                  {/* Public Routes */}
                    <Route path="/" element={<Index />} />
                    <Route path="/sitemap.xml" element={<Sitemap />} />
                    <Route path="/robots.txt" element={<Robots />} />
                    <Route path="/towns" element={<TownsIndex />} />
                    <Route path="/market-value-report" element={<MarketValueReportPage />} />
                    <Route path="/community-guide" element={<CommunityGuidePage />} />
                    <Route path="/mortgages" element={<MortgageInfoPage />} />
                    <Route path="/buyers" element={<BuyersPage />} />
                    <Route path="/sellers" element={<SellersPage />} />
                    <Route path="/blog" element={<BlogIndex />} />
                    <Route path="/blog/:postSlug" element={<BlogPost />} />
                    
                    {/* Admin Routes */}
                    <Route path="/admin/login" element={<AdminLogin />} />
                    <Route path="/admin/setup" element={<Setup />} />
                    <Route path="/admin/reset-password" element={<ResetPassword />} />
                    <Route path="/admin" element={<AdminDashboard />} />
                    <Route path="/agent" element={<AgentDashboard />} />
                    <Route path="/agent/wizard" element={<WizardPage />} />
                    <Route path="/loanofficer" element={<LoanOfficerDashboard />} />
                    <Route path="/admin/profile" element={<AdminProfile />} />
                    <Route path="/admin/settings" element={<SiteSettings />} />
                    <Route path="/admin/towns" element={<TownManage />} />
                    <Route path="/admin/towns/setup" element={<TownSetup />} />
                    <Route path="/admin/towns/:townId/content" element={<TownContentEdit />} />
                    <Route path="/admin/areas" element={<AreaManage />} />
                    <Route path="/admin/areas/setup" element={<AreaSetup />} />
                    <Route path="/admin/areas/:areaId/content" element={<AreaContentEdit />} />
                    <Route path="/admin/blog" element={<BlogManage />} />
                    <Route path="/admin/blog/:postId/edit" element={<BlogEdit />} />
                    <Route path="/admin/blog/:postId/preview" element={<BlogPreview />} />
                    <Route path="/admin/leads" element={<LeadsManage />} />
                    <Route path="/admin/ads" element={<AdsManage />} />
                    <Route path="/admin/pages/home" element={<HomepageEdit />} />
                    <Route path="/admin/pages/:pageType" element={<SitePageEdit />} />
                    <Route path="/admin/searchconsole" element={<SearchConsole />} />
                    <Route path="/admin/loan-officers" element={<LoanOfficersManage />} />
                    <Route path="/admin/loan-officers/new" element={<LoanOfficerEdit />} />
                    <Route path="/admin/loan-officers/:officerId/edit" element={<LoanOfficerEdit />} />
                    
                    {/* Dynamic Public Routes - must be last */}
                    <Route path="/:townSlug" element={<TownPage />} />
                    <Route path="/:townSlug/:areaSlug" element={<AreaPage />} />
                    
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </GoogleAnalyticsProvider>
              </BrowserRouter>
            </TooltipProvider>
          </LoanOfficerAuthProvider>
        </SiteAdminProvider>
      </GlobalAdminProvider>
    </AuthProvider>
  </QueryClientProvider>
</HelmetProvider>
);

export default App;
